import { useCallback, useState } from "react";
import Container from "modules/Core/sub-modules/ui-kit/components/Container";
import Title from "modules/Core/sub-modules/ui-kit/components/Title";
import Text from "modules/Core/sub-modules/ui-kit/components/Text";
import Button from "modules/Core/sub-modules/ui-kit/components/Button";
import { useIsOpen } from "modules/Core/hooks/useIsOpen";
import { useOnLoad } from "modules/Core/hooks/useOnLoad";
import strappedConnected from "modules/Core/higher-order-components/strappedConnected";
import withFocusedBoothId from "modules/booths/higher-order-components/withFocusedBoothId";
import ActivityForm from "modules/cycles/components/ActivityForm";
import AllocateActivityButton from "modules/cycles/components/AllocateActivityButton";
import { ACTIVITY_KIND } from "modules/cycles/consts";
import middleware from "modules/cycles/middleware";
import styles from "./styles.module.scss";

const GatewayTaskRow = ({ activity, onAllocated }) => (
    <Container className={styles.task} flex spaceBetween alignCenter>
        <Container flex col className={styles.taskCopy}>
            <Title Element="h4" className={styles.taskName}>
                {activity?.name || "Untitled task"}
            </Title>
            {activity?.text && (
                <Text className={styles.taskText}>{activity.text}</Text>
            )}
        </Container>
        <AllocateActivityButton
            activityId={activity?.id}
            allocated={Boolean(activity?.allocated)}
            onAllocated={onAllocated}
        />
    </Container>
);

const GatewayTasks = ({ gatewayId, boothId }) => {
    const { isOpen, open, close } = useIsOpen();
    const [tasks, setTasks] = useState([]);

    const load = useCallback(async () => {
        if (!gatewayId) return;
        const result = await middleware.ops.fetchActivities({
            boothId,
            gatewayId,
        });
        setTasks((result?.activities ?? []).filter(
            (activity) => activity?.kind !== ACTIVITY_KIND.GATEWAY
        ));
    }, [boothId, gatewayId]);

    useOnLoad(load, Boolean(gatewayId), [gatewayId, boothId, load]);

    return (
        <Container className={styles.container} flex col>
            <Container className={styles.header} flex spaceBetween alignCenter>
                <Title Element="h4" className={styles.heading}>Tasks</Title>
                <Button onClick={open}>Add task</Button>
            </Container>
            {tasks.length === 0 ? (
                <Text className={styles.empty}>
                    No tasks yet. Add a task for this checkpoint, then place it in a cycle.
                </Text>
            ) : (
                <Container className={styles.list} flex col>
                    {tasks.map((activity) => (
                        <GatewayTaskRow
                            key={activity.id}
                            activity={activity}
                            onAllocated={load}
                        />
                    ))}
                </Container>
            )}
            <ActivityForm
                isOpen={isOpen}
                close={close}
                boothId={boothId}
                gatewayId={gatewayId}
                kind={ACTIVITY_KIND.STANDARD}
                title="Add a task"
                onSuccess={load}
            />
        </Container>
    );
};

export default withFocusedBoothId(strappedConnected(
    GatewayTasks,
    {},
    {},
    ({ gatewayId, boothId }) => ({
        gatewayId,
        boothId,
    })
));
